import { Logger } from '../../../../../logger/src';
import { whenDeviceRegistered } from '../../../../../../packages/widgets/src/services/WebexInstanceService';

const FEATURE_TOGGLE_NAME = 'spark-760515-desktop-msteams-calling-ai-summary';
const AI_SUMMARY_PLUGIN_READY_TIMEOUT_MS = 20000;
const AI_SUMMARY_PLUGIN_POLL_INTERVAL_MS = 500;

/**
 * Wait until the AI Summary plugin is initialized and ready.
 * Polls for plugin availability with timeout protection.
 * Similar to whenDssReady() for DSS plugin.
 *
 * @param timeoutMs - Maximum time to wait in milliseconds (default 20s)
 * @returns {Promise<any>} Resolves with the aisummary plugin instance
 * @throws {Error} If plugin is not available after timeout
 */
export function whenAISummaryReady(timeoutMs: number = AI_SUMMARY_PLUGIN_READY_TIMEOUT_MS): Promise<any> {
  const pollInterval = AI_SUMMARY_PLUGIN_POLL_INTERVAL_MS;
  const start = Date.now();

  // Check if already available
  if (window.webex?.internal?.aisummary && (window as any).isWebexInitalized) {
    Logger.debug('[AISummary] Plugin already ready');
    return Promise.resolve(window.webex.internal.aisummary);
  }

  return new Promise((resolve, reject) => {
    Logger.debug('[AISummary] Waiting for plugin initialization...');
    const timer = setInterval(() => {
      if (window.webex?.internal?.aisummary && (window as any).isWebexInitalized) {
        clearInterval(timer);
        const elapsed = Date.now() - start;
        Logger.debug(`[AISummary] Plugin ready after ${elapsed}ms`);
        resolve(window.webex.internal.aisummary);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(timer);
        const elapsed = Date.now() - start;
        Logger.info(`[AISummary] Plugin initialization timeout after ${elapsed}ms`);
        reject(new Error('whenAISummaryReady: timeout waiting for AI Summary plugin initialization'));
      }
    }, pollInterval);
  });
}

/**
 * Check if AI summary feature is enabled via server-side feature toggle.
 *
 * This function checks the feature toggle on the Webex backend during device registration.
 * The feature gate is centrally managed and can be toggled without code deployment.
 *
 * In MS Teams embedded browser, device registration may be delayed.
 * This function adds timeout protection to avoid hanging indefinitely.
 *
 * @returns {Promise<boolean>} true if feature is enabled, false otherwise
 */
export async function isAISummaryFeatureEnabled(): Promise<boolean> {
  try {
    // Wait for device registration with timeout (30s max)
    const deviceReadyPromise = whenDeviceRegistered();
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Device registration timeout after 30s')), 30000)
    );

    await Promise.race([deviceReadyPromise, timeoutPromise]);
    Logger.debug('[AISummary] Device registered — checking feature toggle');

    // Check feature toggle with timeout (5s max)
    const featurePromise = window.webex.internal.feature.getFeature('developer', FEATURE_TOGGLE_NAME);
    const featureTimeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Feature toggle check timeout after 5s')), 5000)
    );

    const result = await Promise.race([featurePromise, featureTimeoutPromise]);
    const enabled = !!result;
    Logger.info(`[AISummary] Feature toggle "${FEATURE_TOGGLE_NAME}" — enabled: ${enabled}`);
    return enabled;
  } catch (error) {
    Logger.info(
      `[AISummary] Feature toggle "${FEATURE_TOGGLE_NAME}" check failed (may be normal in MS Teams embedded browser): ${error}`
    );
    // Default to disabled if check fails for safety
    return false;
  }
}
